import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiPost, operationsClient } from '@/lib/api';
import { leadEndpoints } from '@/lib/api/endpoints';
import { leadKeys } from '@/lib/queryKeys';
import { useApiMutation } from '../useApiMutation';
import type { AutoAssignLeadResult } from '@/types/lead';

/**
 * Runs the per-tenant assignment engine on a single lead
 * (`POST /leads/:id/auto-assign`). Mirrors `useAssignLead`'s invalidations.
 * The result's `assigned` flag tells the caller whether a rep matched.
 */
export function useAutoAssignLead() {
  const queryClient = useQueryClient();

  return useApiMutation<AutoAssignLeadResult, { leadId: string }>({
    mutationFn: ({ leadId }) =>
      apiPost<AutoAssignLeadResult>(operationsClient, leadEndpoints.autoAssign(leadId)),
    onSuccess: (data, { leadId }) => {
      // Refetch the detail via GET /leads/:id rather than caching the mutation
      // response: only the GET path resolves assignment-history / assignee names
      // for owners who have left the company (NOT-486). Caching the un-resolved
      // response here would flash "Unknown user" until the next refetch.
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: leadKeys.pipeline() });
      if (data.assigned) {
        toast.success('Lead auto-assigned');
      } else {
        toast.info('No rep matched — lead left unassigned');
      }
    },
  });
}
